import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    FeatureGrid,
    FAQList,
    CtaBand,
    PageFooter,
    Dateline,
    DataTable,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/bluebeam-revu-20-end-of-life');

const SOURCES = [
    {
        href: 'https://support.bluebeam.com/revu/resources/revu-20-eol.html',
        label: 'Revu 20 End of Life (EOL) effective December 31, 2026',
    },
    {
        href: 'https://www.bluebeam.com/upgrade-from-revu-20',
        label: 'Bluebeam Revu 20 EOL',
    },
    {
        href: 'https://support.bluebeam.com/legacy/resources/bluebeam-eol-support.html',
        label: 'Bluebeam End of Life (EOL) Support',
    },
] as const;

const TIMELINE = [
    { cells: ['September 30, 2023', 'End of sale', 'Bluebeam stopped selling Revu 20 licenses.'] },
    { cells: ['July 31, 2026', 'End of support', 'Technical support and self-service license management, including seat releases, are no longer available.'] },
    { cells: ['December 31, 2026', 'End of life', 'Revu 20 loses access to Studio Sessions and Projects, including through integrations.'] },
] as const;

const CAPABILITIES = [
    {
        title: 'Open a PDF on your device',
        body: 'Choose a PDF from your device. PDF Markup Tool opens and processes it in your browser without uploading the file or requiring an account.',
    },
    {
        title: 'Continue with existing annotations',
        body: 'The PDF import supports standard line, polyline, polygon, rectangle, ellipse, freehand ink, text, and highlight annotations. It also recognizes arrow, cloud, and callout variants in supported PDF annotations.',
    },
    {
        title: 'Mark up and measure drawings',
        body: 'Use line, polyline, dimension, polygon, rectangle, ellipse, freehand, arrow, highlight, cloud, callout, count, stamp, and text tools. Calibrate a drawing scale to measure length, and measure area on supported shapes.',
    },
    {
        title: 'Export your markups',
        body: 'Export an annotated PDF or save markup data as a CSV file.',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Will Revu 20 stop working after December 31, 2026?',
        answer: ['Bluebeam says perpetual Revu 20 licenses can continue running locally after that date, but they will no longer connect to Bluebeam cloud services. Studio Sessions and Projects access ends.'],
    },
    {
        question: 'What changed on July 31, 2026?',
        answer: ['Revu 20 reached end of support. Bluebeam no longer provides technical support or self-service license management, including seat releases.'],
    },
    {
        question: 'Can I open PDFs I marked up in Revu?',
        answer: ['PDF Markup Tool imports supported standard PDF annotations, including lines, shapes, freehand ink, text, highlights, and supported arrow, cloud, and callout variants.'],
    },
    {
        question: 'Can I keep marking up a PDF in a browser?',
        answer: ['Yes. Open a PDF locally in PDF Markup Tool to add markups, calibrate a drawing scale, measure, and export an annotated PDF or CSV markup list.'],
    },
] as const;

export function BluebeamRevu20EndOfLifePage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="Revu 20 support timeline"
                    title={<>Bluebeam Revu 20 end of life:<br />what changes and when</>}
                    lead="Bluebeam Revu 20 reached end of support on July 31, 2026, and reaches end of life on December 31, 2026. Perpetual Revu 20 keeps running locally after that date, while Bluebeam cloud services end."
                    cta={<Button href="/" variant="primary" size="lg">Keep marking up drawings in your browser</Button>}
                    chips={['End of support: July 31, 2026', 'End of life: December 31, 2026', 'Perpetual licenses continue running locally']}
                />
                <ContextNote text="Bluebeam's timeline distinguishes end of support from end of life. Revu 20 does not simply stop launching: perpetual license holders can continue to run it locally, while support and cloud access change on the dates below." />
                <section className="mktg-context">
                    <div className="mktg-container">
                        <h2 className="mktg-section-title">Revu 20 end-of-life timeline</h2>
                        <p className="mktg-section-lead">The dates and changes below are from Bluebeam's published support information.</p>
                        <DataTable
                            caption="Bluebeam Revu 20 support dates"
                            columns={['Date', 'Milestone', 'What changes']}
                            rows={TIMELINE}
                        />
                        <p>Perpetual Revu 20 continues to run locally after December 31, 2026; it will no longer connect to Bluebeam cloud services.</p>
                    </div>
                </section>
                <FeatureGrid
                    sectionTitle="What you can do in PDF Markup Tool today"
                    sectionLead="Open a drawing in your browser, work with supported PDF annotations, and export your markups."
                    cards={CAPABILITIES}
                />
                <section className="mktg-context">
                    <div className="mktg-container">
                        <h2 className="mktg-section-title">Sources</h2>
                        <ul>
                            {SOURCES.map((source) => (
                                <li key={source.href}>
                                    <a href={source.href}>{source.label}</a> (checked 2026-11-01)
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Keep marking up drawings in your browser"
                    lead="Open a local PDF, add markups, measure to scale, and export your annotations."
                    actions={
                        <>
                            <Button href="/" variant="primary" size="lg">Open a PDF</Button>
                            <Button href="/redline-pdf-drawing/" variant="ghost">How to redline a PDF drawing</Button>
                            <Button href="/measure-pdf-online/" variant="ghost">Measure a PDF online</Button>
                        </>
                    }
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="PDF markup for drawing review" />
        </MarketingPage>
    );
}
